import { TaskActualWorkApprovalStatusGridOptions } from './gridOptions'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import { PageArea, PageProps } from '../index'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import DateVO from '../../../vo/DateVO'
import Header, { ApprovalSummary } from './Header'
import { useTaskActualWorkApprovalStatusData } from './hooks/TaskActualWorkApproveStatusData'
import { usePageState } from './hooks/pageState'
import ProjectClosingDialog from './components/ProjectClosingDialog'
import { useProjectClosingDialogs } from './hooks/projectClosingDialog'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  ICellRendererParams,
  SortChangedEvent,
} from 'ag-grid-community'
import { useExcel } from './hooks/excel'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useDialog } from './hooks/dialog'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { intl } from '../../../i18n'
import {
  useBulkSheetState,
  RestoreType,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import {
  RequestStatus,
  TaskActualWorkApprovalStatusRow,
} from './taskActualWorkApprovalStatus'

type Props = PageProps

const ProjectTaskActualWorkApprovalStatus = (props: Props) => {
  const functionUuid = props.uuid
  const { project } = useProjectPrivateContext()
  const pageState = usePageState({ functionUuid: props.uuid })
  const { handleMonthChange, yearMonthQuery, displayDateTerm } = pageState

  const projectUuid = useMemo(() => project?.uuid, [project?.uuid])

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [isStateRestored, setIsStateRestored] = useState<boolean>(false)
  const restoreTypes = useMemo<RestoreType[]>(
    () => ['COLUMN_STATE', 'FILTER_STATE'],
    []
  )

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    'TaskActualWorkApprovalStatus',
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    restoreTypes,
    projectUuid
  )

  const ref = useRef<HTMLDivElement>(null)
  const gridOptions = useMemo(() => {
    return TaskActualWorkApprovalStatusGridOptions()
  }, [])

  const gridContext = useMemo(
    () => ({
      decimalScaleUnit: pageState.decimalScaleUnit,
    }),
    [pageState.decimalScaleUnit]
  )

  const {
    fetchRecords,
    data,
    fetchClosing,
    closingStatus,
    totalRow,
    loading: dataLoading,
  } = useTaskActualWorkApprovalStatusData()

  const loading = useMemo(() => {
    return !projectUuid || dataLoading
  }, [projectUuid, dataLoading])

  useEffect(() => {
    if (!gridOptions.api) return
    gridOptions.context = gridContext
    gridOptions.api.refreshCells({ force: true })
  }, [gridContext, gridOptions])

  const { dialogOpen, openDialog, closeDialog, onExportExcel } = useExcel(
    gridOptions,
    gridContext
  )
  const columnSetting = useColumnSetting()
  const dialogState = useDialog()
  const [filteredData, setFilteredData] = useState<
    TaskActualWorkApprovalStatusRow[]
  >([])

  const updateFilteredSummary = useCallback(() => {
    if (!gridOptions.api || !data || data.length === 0) {
      setFilteredData(data || [])
      return
    }

    const filteredNodes: any[] = []
    gridOptions.api.forEachNodeAfterFilter((node: any) => {
      if (node.data) {
        filteredNodes.push(node.data)
      }
    })

    setFilteredData(filteredNodes)
  }, [gridOptions.api, data])

  useEffect(() => {
    updateFilteredSummary()
  }, [data, updateFilteredSummary])

  const approvalSummary = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return undefined

    let unApproved = 0
    let unRequested = 0
    let actualWorkHourEmpty = 0
    let taskActualWorkEmpty = 0
    let workingTaskHoursMismatch = 0

    filteredData.forEach(row => {
      if (row.requestStatus === RequestStatus.UNAPPROVED) {
        unApproved++
      }
      if (row.requestStatus === RequestStatus.UNREQUESTED) {
        unRequested++
      }
      if (row.discrepancyActualWorkingHour) {
        actualWorkHourEmpty++
      }
      if (row.discrepancyTaskActualWork) {
        taskActualWorkEmpty++
      }
      if (row.discrepancyBetweenActualWorkingHourAndTaskActualWork) {
        workingTaskHoursMismatch++
      }
    })

    return {
      unApproved,
      unRequested,
      actualWorkHourEmpty,
      taskActualWorkEmpty,
      workingTaskHoursMismatch,
    } as ApprovalSummary
  }, [filteredData])

  const refresh = useCallback(async () => {
    if (!projectUuid || !pageState.initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    await fetchClosing(projectUuid, startDate!)
    await fetchRecords(projectUuid, startDate!, endDate!)
  }, [
    projectUuid,
    pageState.initialized,
    displayDateTerm.startDate,
    pageState.endDate,
    fetchClosing,
    fetchRecords,
  ])

  useEffect(() => {
    if (projectUuid && pageState.initialized) {
      refresh()
    }
  }, [project.uuid, fetchRecords, refresh, pageState.initialized, projectUuid])

  // create user name link
  useEffect(() => {
    if (!gridOptions.api || !gridOptions.columnApi || !pageState.initialized) {
      return
    }
    const col = gridOptions.columnApi.getColumn('userName')
    if (!col) return
    const colDef = col.getColDef()
    colDef.cellRendererParams = (params: ICellRendererParams) => ({
      labelField: params.node.data.userName,
      iconUrlField: params.node.data.userIconUrl,
      url: `/taskActualWorkApprove/${project?.code}?userCode=${params.node.data.code}&${yearMonthQuery}`,
    })
    gridOptions.api.refreshCells({ force: true, columns: ['userName'] })
  }, [gridOptions, yearMonthQuery, pageState.initialized, data, project?.code])

  const {
    showProjectClosingDialog,
    onClickProjectClosing,
    handleProjectClosingDialogSubmit,
    handleProjectClosingDialogClose,
  } = useProjectClosingDialogs(
    project,
    pageState,
    closingStatus,
    { startDateString: displayDateTerm.startDate || '' },
    fetchClosing,
    functionUuid
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterState = e.api.getFilterModel()
      bulkSheetState.saveFilterState(filterState)
      setFilteredColumns(
        Object.keys(filterState)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v)
      )
      updateFilteredSummary()
    },
    [bulkSheetState, updateFilteredSummary]
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridOptions.columnApi?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState, gridOptions.columnApi])

  const onSortedColumnsStateChanged = useCallback(
    (e: SortChangedEvent) => {
      const sortedColumns = e.columnApi
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => {
          return {
            ...e.api.getColumnDef(col.colId),
            colId: col.colId,
          }
        })
        .filter(v => !!v) as ColDef[]

      const columnState = gridOptions.columnApi?.getColumnState()
      const sortedColumnState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedColumnState[state.colId] = state
          }
        })

      const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
        }
      })

      setSortColumnsState(sortedColumnsState)
    },
    [gridOptions.columnApi]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      if (e.api && e.columnApi) {
        rememberColumnState()
        onSortedColumnsStateChanged(e)
      }
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridOptions.api) return
      const filterModel = gridOptions.api.getFilterModel()
      delete filterModel[column.colId || column.field || '']
      gridOptions.api.setFilterModel(filterModel)
    },
    [gridOptions.api]
  )

  const resetFilters = useCallback(() => {
    if (!gridOptions.api) return
    gridOptions.api.setFilterModel([])
  }, [gridOptions.api])

  const onGridReady = useCallback(
    params => {
      gridOptions.api = params.api
      gridOptions.columnApi = params.columnApi

      if (!_.isEmpty(bulkSheetState.columnState)) {
        gridOptions.columnApi?.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
      setIsStateRestored(true)
    },
    [bulkSheetState.columnState, gridOptions]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridOptions.api?.setFilterModel(bulkSheetState.filterState)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState, gridOptions.api])

  const onDeleteSortedColumn = useCallback((colId: string | ColDef<any>) => {
    if (!gridOptions.columnApi) return

    gridOptions.columnApi.applyColumnState({
      state: [{ colId: colId.toString(), sort: null }],
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridOptions.columnApi) return

    gridOptions.columnApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridOptions.columnApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridOptions.columnApi) return

      gridOptions.columnApi.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridOptions.columnApi]
  )

  const getCurrentUIState = useCallback(() => {
    return {
      columnState: bulkSheetState.columnState,
      filterState: bulkSheetState.filterState,
    }
  }, [bulkSheetState.columnState, bulkSheetState.filterState])

  const applyUIState = useCallback(
    (uiState: any) => {
      if (uiState.columnState) {
        bulkSheetState.saveColumnState(uiState.columnState)
        if (!_.isEmpty(uiState.columnState) && gridOptions.columnApi) {
          gridOptions.columnApi.applyColumnState({
            state: uiState.columnState,
            applyOrder: true,
          })
        }
      }
      if (uiState.filterState) {
        bulkSheetState.saveFilterState(uiState.filterState)
        if (!_.isEmpty(uiState.filterState) && gridOptions.api) {
          gridOptions.api.setFilterModel(uiState.filterState)
        }
      }
      if (uiState.filterState && Object.keys(uiState.filterState).length > 0) {
        const filterKeys = Object.keys(uiState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      } else {
        setFilteredColumns([])
      }

      if (uiState.columnState && uiState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] = uiState.columnState
          .filter(col => col.sort)
          .map(col => ({
            colId: col.colId,
            sort: col.sort!,
          }))
        setSortColumnsState(sortedColumnsState)
      } else {
        setSortColumnsState([])
      }
    },
    [
      bulkSheetState,
      gridOptions.columnApi,
      gridOptions.api,
      gridOptions.columnDefs,
    ]
  )

  useEffect(() => {
    if (
      bulkSheetState.initialized &&
      bulkSheetState.restored &&
      !isStateRestored
    ) {
      if (
        bulkSheetState.filterState &&
        Object.keys(bulkSheetState.filterState).length > 0
      ) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }

      if (bulkSheetState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] =
          bulkSheetState.columnState
            .filter(col => col.sort)
            .map(col => ({
              colId: col.colId,
              sort: col.sort!,
            }))
        setSortColumnsState(sortedColumnsState)
      }
    }
  }, [
    bulkSheetState.initialized,
    bulkSheetState.restored,
    bulkSheetState.filterState,
    bulkSheetState.columnState,
    gridOptions.columnDefs,
    isStateRestored,
  ])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  if (!pageState.initialized || !bulkSheetState.initialized) {
    return (
      <PageArea>
        <Loading isLoading={true} />
      </PageArea>
    )
  }

  return (
    <PageArea>
      <Header
        loading={loading}
        refresh={refresh}
        onMonthChange={handleMonthChange}
        isClosing={closingStatus.closed}
        closedAt={closingStatus.closedAt}
        onClickProjectClosing={onClickProjectClosing}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        onClickExport={openDialog}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={(value: number) => {
          pageState.setRowHeight(value)
        }}
        approvalSummary={approvalSummary}
        decimalScaleUnit={pageState.decimalScaleUnit}
        onChangeDecimalScaleUnit={pageState.setDecimalScaleUnit}
        endDate={pageState.endDate}
        isCurrentMonth={pageState.isCurrentMonth}
        onEndDateChange={pageState.setEndDate}
        endDatePreset={pageState.endDatePreset}
        setEndDatePreset={pageState.setEndDatePreset}
        displayMonth={new DateVO(pageState.displayDateTerm.startDate)}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        // Unnecessary, but may be reimplemented in the future.
        // pinnedTopRowData={totalRow ? [totalRow] : []}
        {...gridOptions}
        context={gridContext}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onColumnMoved={rememberColumnState}
        onColumnResized={rememberColumnState}
        onColumnVisible={rememberColumnState}
        rowHeight={pageState.rowHeight}
      />
      <Loading isLoading={loading} elem={ref.current} />
      <ProjectClosingDialog
        isOpen={showProjectClosingDialog}
        onSubmit={handleProjectClosingDialogSubmit}
        onClose={handleProjectClosingDialogClose}
        isCancel={closingStatus.closed}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        columnApi={gridOptions?.columnApi ?? undefined}
        gridApi={gridOptions?.api ?? undefined}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={dialogState.openUiState}
        initializeColumnState={() => {
          if (!gridOptions.columnApi || !gridOptions.api) return
          gridOptions.columnApi.resetColumnState()
          gridOptions.api.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          setFilteredColumns([])
          setSortColumnsState([])
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={dialogState.uiState}
        onClose={dialogState.closeUiState}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState || selectedUIState
          applyUIState(uiStateToApply)
          dialogState.closeUiState()
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'bulksheet.toolbar.columnQuickFilter.label.restore',
        })}
        sharable={false}
        currentUIState={getCurrentUIState()}
      />
      {dialogOpen && (
        <MultiSelectDialog
          onClose={closeDialog}
          onSubmit={(allList, selectedItem) => {
            const selectedColIds = selectedItem.map(col => col.value)
            onExportExcel(selectedColIds)
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridOptions.columnApi?.getColumns() || [])
              .filter(
                column =>
                  !['uuid', 'drag', 'action', 'select', 'rowNumber'].includes(
                    column.getColId()
                  )
              )
              .map(column => {
                return {
                  value: column.getColId(),
                  displayName:
                    gridOptions.columnApi?.getDisplayNameForColumn(
                      column,
                      null
                    ) ?? '',
                  defaultChecked:
                    column.isVisible() ||
                    column.getColDef().cellRendererParams?.uiMeta?.tree,
                }
              })
          }}
          hideIcon={true}
        />
      )}
    </PageArea>
  )
}

export default projectPrivate(ProjectTaskActualWorkApprovalStatus)
